import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '../services/auth.service';

/**
 * Flexible guard that allows access based on multiple roles
 * Usage: canActivate: [multiRoleGuard(['admin', 'doctor', 'nurse', 'assistant'])]
 */
export function multiRoleGuard(
  allowedRoles: ('admin' | 'doctor' | 'nurse' | 'assistant')[]
): CanActivateFn {
  return () => {
    const authService = inject(AuthService);
    const router = inject(Router);

    // Check if user is authenticated
    if (!authService.isAuthenticated()) {
      router.navigate(['/login']);
      return false;
    }

    // Get simplified user role
    const userRole = authService.getSimplifiedRole();

    // Check if user role is in the allowed roles list
    if (userRole && allowedRoles.includes(userRole)) {
      return true;
    }

    // Redirect to unified dashboard if access is denied
    if (userRole && ['admin', 'assistant', 'doctor', 'nurse'].includes(userRole)) {
      router.navigate(['/dashboard']);
    } else {
      router.navigate(['/login']);
    }

    return false;
  };
}

/**
 * Pre-configured guards for common combinations
 */
export const adminOnlyGuard = multiRoleGuard(['admin', 'assistant']);
export const doctorOnlyGuard = multiRoleGuard(['doctor']);
export const nurseOnlyGuard = multiRoleGuard(['nurse']);
export const adminDoctorGuard = multiRoleGuard(['admin', 'assistant', 'doctor']);
export const doctorNurseGuard = multiRoleGuard(['doctor', 'nurse']);
export const allRolesGuard = multiRoleGuard(['admin', 'assistant', 'doctor', 'nurse']);
